import { useMemo, useCallback } from "react";
import watermindsLogo from "../../../assets/waterminds-logo.png";
import GenericForm from "common/Components/GenericForm/GenericForm";
import {
  StyledPage,
  StyledAuthMain,
  StyledSignupContainer,
  StyledCard,
  StyledSignupCard,
  StyledSignupSuccessCard,
  StyledSignupSuccessBand,
  StyledSignupCardHeader,
  StyledSignupForm,
  StyledSignupFieldsScroll,
  StyledAuthFormFields,
  StyledSignupFormFooter,
  StyledAuthSuccessBand,
  StyledAuthSuccessMark,
  StyledAuthSuccessBandText,
  StyledAuthSuccessBody,
  StyledAuthSuccessRedirectMessage,
  StyledAuthSrOnly,
  StyledWatermindsLogo,
  StyledTitle,
  StyledSubtitle,
  StyledErrorMessage,
  StyledSignupSubmitButton,
  SignupTwoColumnRow,
} from "../AuthPageLayout.styles";
import { SIGNUP_UI } from "./signup.constants";
import { useSignup } from "./Signup.hook";

function SignupStatusCard({ title, children }) {
  return (
    <StyledPage>
      <StyledAuthMain>
        <StyledSignupContainer>
          <StyledCard>
            <StyledWatermindsLogo src={watermindsLogo} alt="Waterminds Logo" />
            {title ? (
              <StyledTitle variant="h5" component="h2">
                {title}
              </StyledTitle>
            ) : null}
            {children}
          </StyledCard>
        </StyledSignupContainer>
      </StyledAuthMain>
    </StyledPage>
  );
}

function SignupInviteExpiredCard() {
  return (
    <SignupStatusCard title={SIGNUP_UI.inviteExpiredTitle}>
      <StyledSubtitle component="p">{SIGNUP_UI.inviteExpiredMessage}</StyledSubtitle>
    </SignupStatusCard>
  );
}

function SignupActiveSessionCard({ onLogout, logoutLoading }) {
  return (
    <SignupStatusCard title={SIGNUP_UI.activeSessionTitle}>
      <StyledSubtitle component="p">{SIGNUP_UI.activeSessionMessage}</StyledSubtitle>
      <StyledSignupFormFooter>
        <StyledSignupSubmitButton
          type="button"
          variant="contained"
          onClick={onLogout}
          disabled={logoutLoading}
        >
          {logoutLoading ? SIGNUP_UI.loggingOutLabel : SIGNUP_UI.logoutLabel}
        </StyledSignupSubmitButton>
      </StyledSignupFormFooter>
    </SignupStatusCard>
  );
}

export default function Signup() {
  const {
    invite,
    sessionCheck,
    logoutLoading,
    handleLogoutForSignup,
    tokenValidation,
    canShowSignupForm,
    formValues,
    errors,
    bannerError,
    loading,
    availabilityChecking,
    success,
    handleChange,
    handleFieldBlur,
    submit,
  } = useSignup();

  const formDisabled = loading || availabilityChecking;

  const withDisabled = useCallback(
    (field) => ({
      ...field,
      disabled: formDisabled || field.disabled,
    }),
    [formDisabled]
  );

  const signupSections = useMemo(() => {
    const all = SIGNUP_UI.signupFields;
    const pick = (name) => all.find((f) => f.name === name);
    return [
      {
        wrapper: SignupTwoColumnRow,
        fields: [
          withDisabled(pick("email")),
          withDisabled(pick("phoneNumber")),
        ],
      },
      {
        wrapper: SignupTwoColumnRow,
        fields: [
          withDisabled(pick("firstName")),
          withDisabled(pick("lastName")),
        ],
      },
      {
        wrapper: SignupTwoColumnRow,
        fields: [
          withDisabled(pick("password")),
          withDisabled(pick("confirmPassword")),
        ],
      },
      {
        fields: [withDisabled(pick("acceptedTerms"))],
      },
    ];
  }, [withDisabled]);

  if (sessionCheck === "loading") {
    return (
      <SignupStatusCard title={SIGNUP_UI.title}>
        <StyledSubtitle>{SIGNUP_UI.sessionCheckMessage}</StyledSubtitle>
      </SignupStatusCard>
    );
  }

  if (sessionCheck === "active") {
    return (
      <SignupActiveSessionCard
        onLogout={handleLogoutForSignup}
        logoutLoading={logoutLoading}
      />
    );
  }

  if (!invite.isValid) {
    return <SignupInviteExpiredCard />;
  }

  const isValidatingInvite =
    tokenValidation.status === "loading" || tokenValidation.status === "idle";

  if (isValidatingInvite) {
    return (
      <SignupStatusCard title={SIGNUP_UI.title}>
        <StyledSubtitle>{SIGNUP_UI.validatingMessage}</StyledSubtitle>
      </SignupStatusCard>
    );
  }

  if (tokenValidation.status === "error") {
    return <SignupInviteExpiredCard />;
  }

  if (
    tokenValidation.status === "ready" &&
    !tokenValidation.requiresSignup
  ) {
    return <SignupInviteExpiredCard />;
  }

  if (!canShowSignupForm && !success) {
    return null;
  }

  const SignupCardShell = success ? StyledSignupSuccessCard : StyledSignupCard;

  return (
    <StyledPage>
      <StyledAuthMain>
        <StyledSignupContainer>
          <SignupCardShell>
            <StyledSignupCardHeader>
              <StyledWatermindsLogo src={watermindsLogo} alt="Waterminds Logo" />
              <StyledTitle variant="h5" component="h2">
                {SIGNUP_UI.title}
              </StyledTitle>
              <StyledSubtitle>{SIGNUP_UI.subtitle}</StyledSubtitle>
            </StyledSignupCardHeader>
            {success ? (
              <>
                <StyledSignupSuccessBand role="status">
                  <StyledAuthSuccessMark aria-hidden="true">✓</StyledAuthSuccessMark>
                  <StyledAuthSuccessBandText component="span">
                    <StyledAuthSrOnly>Success: </StyledAuthSrOnly>
                    {SIGNUP_UI.successBannerTitle}
                  </StyledAuthSuccessBandText>
                </StyledSignupSuccessBand>
                <StyledAuthSuccessBody component="p" sx={{ marginBottom: "12px" }}>
                  {SIGNUP_UI.successBody}
                </StyledAuthSuccessBody>
              </>
            ) : (
              <StyledSignupForm noValidate onSubmit={submit}>
                <StyledSignupFieldsScroll>
                  <StyledAuthFormFields>
                    <GenericForm
                      sections={signupSections}
                      values={formValues}
                      onChange={handleChange}
                      onFieldBlur={handleFieldBlur}
                      errors={errors}
                      showBorder={false}
                    />
                  </StyledAuthFormFields>
                </StyledSignupFieldsScroll>
                <StyledSignupFormFooter>
                  {(bannerError || errors.submit) && (
                    <StyledErrorMessage component="p" sx={{ marginTop: 0 }}>
                      {bannerError || errors.submit}
                    </StyledErrorMessage>
                  )}
                  <StyledSignupSubmitButton
                    type="submit"
                    variant="contained"
                    disabled={formDisabled}
                    sx={{ marginTop: bannerError || errors.submit ? 2 : 0 }}
                  >
                    {loading ? SIGNUP_UI.submittingLabel : SIGNUP_UI.submitLabel}
                  </StyledSignupSubmitButton>
                </StyledSignupFormFooter>
              </StyledSignupForm>
            )}
          </SignupCardShell>
        </StyledSignupContainer>
      </StyledAuthMain>
    </StyledPage>
  );
}
